"use client";

import { useState, useEffect, useRef } from "react";

interface UseTooltipFadeOptions {
  delay?: number;
  shouldShow?: (value: string) => boolean;
}

interface UseTooltipFadeReturn {
  showTooltip: boolean;
  handleMouseEnter: (value: string) => void;
  handleMouseLeave: () => void;
  handleTooltipOpenChange: (open: boolean) => void;
  hideTooltip: () => void;
}

export const useTooltipFade = ({
  delay = 2000,
  shouldShow = (value: string) => !value || value.trim() === "",
}: UseTooltipFadeOptions = {}): UseTooltipFadeReturn => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMouseInFieldRef = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = (value: string) => {
    // Mark that mouse is in the field
    isMouseInFieldRef.current = true;
    
    if (fadeOutTimeoutRef.current) {
      clearTimeout(fadeOutTimeoutRef.current);
      fadeOutTimeoutRef.current = null;
    }
    
    // Only show tooltip if condition is met (e.g., field is empty)
    if (shouldShow(value)) {
      tooltipTimeoutRef.current = setTimeout(() => {
        // Only show tooltip if mouse is still in the field after delay
        if (isMouseInFieldRef.current) {
          setShowTooltip(true);
        }
      }, delay);
    }
  };

  const handleMouseLeave = () => {
    // Mark that mouse has left the field
    isMouseInFieldRef.current = false;
    
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    // Trigger fade-out by setting open to false
    // Radix UI will handle the animation, but we need to ensure it completes
    setShowTooltip(false);
  };

  const handleTooltipOpenChange = (open: boolean) => {
    // Only allow Radix UI to close the tooltip, not open it
    // We control when it opens via our timeout logic
    if (!open) {
      setShowTooltip(false);
    }
  };

  const hideTooltip = () => {
    // Mark that mouse is no longer in field (user is typing)
    isMouseInFieldRef.current = false;
    
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  return {
    showTooltip,
    handleMouseEnter,
    handleMouseLeave,
    handleTooltipOpenChange,
    hideTooltip,
  };
};

